import { createContext, useContext, useEffect, useLayoutEffect, useMemo, type ReactNode } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import type { Locale } from './locale'
import { localizedPath, localeFromPath, stripLocale, localizedRoutes } from './paths'
import messages from './ja.json'
import { pageDescriptions } from './metadata'

const japanese: Record<string, string> = messages
export type Translate = (message: string, values?: Record<string, string | number>) => string
const LocaleContext = createContext({
  locale: 'en' as Locale,
  preference: 'en' as Locale,
  setLanguagePreference: (_locale: Locale) => {},
})
const useBrowserLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect

export function translate(locale: Locale, message: string, values: Record<string, string | number> = {}) {
  const text = locale === 'ja' ? japanese[message] ?? message : message
  return text.replace(/\{(\w+)\}/g, (token, key: string) => String(values[key] ?? token))
}

export function LocaleProvider({ children, locale: override }: { children: ReactNode; locale?: Locale }) {
  const { pathname, search, hash } = useLocation()
  const navigate = useNavigate()
  const locale = override ?? localeFromPath(pathname)
  const preference = locale
  const setLanguagePreference = (next: Locale) => {
    const base = stripLocale(pathname)
    // Authored posts have one URL; switching language returns to their listing.
    const target = /^\/(blog|newsroom)\//.test(base) ? '/' + base.split('/')[1] : base
    navigate(localizedPath(target, next) + search + hash)
  }
  useBrowserLayoutEffect(() => {
    const backstage = pathname.startsWith('/backstage/')
    document.documentElement.lang = backstage ? 'ja' : locale
    if (backstage) delete document.documentElement.dataset.publicLocale
    else document.documentElement.dataset.publicLocale = locale
    delete document.documentElement.dataset.localePending
  }, [locale, pathname])
  useEffect(() => {
    const path = stripLocale(pathname).replace(/\/+$/, '') || '/'
    if (path.startsWith('/backstage/')) return
    const managed: HTMLLinkElement[] = []
    const canonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]') ?? document.createElement('link')
    canonical.rel = 'canonical'
    canonical.href = 'https://bybeit.app' + localizedPath(path, locale)
    if (!canonical.isConnected) document.head.append(canonical)
    document.querySelectorAll('link[rel="alternate"][hreflang]').forEach(link => link.remove())
    if (localizedRoutes.includes(path)) {
      for (const [language, targetLocale] of [['en', 'en'], ['ja', 'ja'], ['x-default', 'en']] as const) {
        const link = document.createElement('link')
        link.rel = 'alternate'; link.hreflang = language
        link.href = 'https://bybeit.app' + localizedPath(path, targetLocale)
        document.head.append(link); managed.push(link)
      }
    }
    const ogUrl = document.querySelector<HTMLMetaElement>('meta[property="og:url"]')
    if (ogUrl) ogUrl.content = canonical.href
    if (/^\/(blog|newsroom)\//.test(path)) return
    const description = translate(locale, pageDescriptions[path] ?? 'The page you are looking for could not be found.')
    const selectors = ['meta[name="description"]', 'meta[property="og:description"]']
    const previous = selectors.map(selector => {
      const element = document.querySelector<HTMLMetaElement>(selector)
      const content = element?.content
      if (element) element.content = description
      return { element, content }
    })
    return () => {
      managed.forEach(link => link.remove())
      previous.forEach(({ element, content }) => {
        if (element && content !== undefined) element.content = content
      })
    }
  }, [locale, pathname])
  return <LocaleContext.Provider value={{ locale, preference, setLanguagePreference }}>{children}</LocaleContext.Provider>
}

export function useLocale() {
  const { locale, preference, setLanguagePreference } = useContext(LocaleContext)
  const t = useMemo<Translate>(() => (message, values) => translate(locale, message, values), [locale])
  return { locale, t, preference, setLanguagePreference }
}
